/**
 * AppSidebar Component
 *
 * Universal sidebar component that adapts to user type (learner, employer, platform).
 * Uses shadcn sidebar components with centralized navigation configuration.
 *
 * Pattern: Single Responsibility - One sidebar adapts to all user types
 * Mobile: Automatically handled by shadcn Sheet integration
 * Filtering:
 * - Learner: All items visible
 * - Employer: Filter by is_primary_representative (adminOnly flag)
 * - Platform: Filter by RBAC permissions (permission flag)
 */

import { useEffect, useState } from 'react';
import { hasPermission } from '@/lib/authorization-utils';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  SidebarHeader,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { getNavigationConfig, type NavItem } from '@/config/navigation';
import type { UserType } from '@/config/routes';
import { getPrimaryUserType } from '@/config/routes';
import { useUser } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useSidebar } from '@/lib/sidebar-utils';
import { Button } from '@/components/ui/button';

export function AppSidebar() {
  const location = useLocation();
  const userQuery = useUser();
  const auth = userQuery.data;
  const user = auth?.user ?? null;
  const session = auth?.session ?? null;

  // User type state
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);

  // Employer admin status (only relevant for employer users)
  const [isEmployerAdmin, setIsEmployerAdmin] = useState<boolean | null>(null);

  // Sidebar state
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  const isExpanded = state === "expanded";

  // Close mobile sidebar when a navigation link is clicked (mobile only)
  const handleMobileNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Fetch user types - only when both user AND session exist
  useEffect(() => {
    const fetchUserTypes = async () => {
      if (!user || !session) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const { data, error } = await supabase
          .rpc('get_user_types')
          .returns<string[]>();

        if (error) {
          console.error('[AppSidebar] Error fetching user types:', error);
          return;
        }

        if (data) {
          const validUserTypes = data.filter(
            (type): type is UserType =>
              type === 'learner' || type === 'employer' || type === 'platform'
          );
          setUserTypes(validUserTypes);
        }
      } catch (err) {
        console.error('[AppSidebar] Unexpected error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!userQuery.isLoading) {
      fetchUserTypes();
    }
  }, [user, session, userQuery.isLoading]);

  // Fetch employer admin status if user is employer - only when session exists
  useEffect(() => {
    const fetchEmployerRole = async () => {
      if (!user?.id || !session || !userTypes.includes('employer')) {
        setIsEmployerAdmin(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('employer_user_profiles')
          .select('is_primary_representative')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[AppSidebar] Error fetching employer role:', error);
          setIsEmployerAdmin(false);
          return;
        }

        setIsEmployerAdmin(data?.is_primary_representative ?? false);
      } catch (err) {
        console.error('[AppSidebar] Unexpected error fetching employer role:', err);
        setIsEmployerAdmin(false);
      }
    };

    fetchEmployerRole();
  }, [user, session, userTypes]);

  // Filter navigation items based on user type and permissions
  const filterByAccess = (item: NavItem): boolean => {
    const primaryType = getPrimaryUserType(userTypes);

    // Employer: Check adminOnly flag
    if (primaryType === 'employer' && item.adminOnly) {
      return isEmployerAdmin === true;
    }

    // Platform: Check permission flag
    if (primaryType === 'platform' && item.permission !== undefined) {
      // permission: null means always visible
      if (item.permission === null) {
        return true;
      }
      // Check if user has the required permission
      if (auth?.permissions && item.permission) {
        return hasPermission(auth.permissions, item.permission);
      }
      return false;
    }

    // Learner: All items visible
    return true;
  };

  // Loading state
  if (userQuery.isLoading || loading) {
    return (
      <Sidebar collapsible="icon">
        <SidebarHeader className="p-4">
          <Skeleton className="h-8 w-32" />
        </SidebarHeader>
        <SidebarContent className="gap-4 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="mb-2 h-3 w-24" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </SidebarContent>
        <SidebarRail />
      </Sidebar>
    );
  }

  // Get navigation config for user type
  const navConfig = getNavigationConfig(userTypes);

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <Button
  onClick={toggleSidebar}
  className="ml-auto flex size-8 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
  variant="ghost"
  size="icon"
>
  {isExpanded ? (
    <ChevronLeft className="size-4" />
  ) : (
    <ChevronRight className="size-4" />
  )}
</Button>
        {/* Dashboard Link - Always first */}
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={navConfig.dashboard.label}
                isActive={location.pathname === navConfig.dashboard.to}
              >
                <NavLink to={navConfig.dashboard.to} onClick={handleMobileNavClick}>
                  <navConfig.dashboard.icon />
                  <span>{navConfig.dashboard.label}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Navigation Sections */}
        {navConfig.sections.map((section) => {
          const visibleItems = section.items.filter(filterByAccess);

          // Hide empty sections
          if (visibleItems.length === 0) {
            return null;
          }

          return (
            <SidebarGroup key={section.title}>
              <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
              <SidebarMenu>
                {visibleItems.map((item) => {
                  // Check if this item has children
                  const hasChildren = item.children && item.children.length > 0;

                  // Check if any child route is active
                  const isChildActive = hasChildren && item.children?.some(
                    (child) => location.pathname === child.to || location.pathname.startsWith(child.to + '/')
                  );

                  // Item with children - render as collapsible
                  // Separate click targets: icon/text navigates, chevron toggles submenu
                  if (hasChildren) {
                    return (
                      <Collapsible
                        key={item.to}
                        asChild
                        defaultOpen={isChildActive}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            tooltip={item.label}
                            isActive={location.pathname === item.to || isChildActive}
                          >
                            <div className="flex w-full items-center">
                              <NavLink
                                to={item.to}
                                className="flex flex-1 items-center gap-2"
                                onClick={handleMobileNavClick}
                              >
                                <item.icon />
                                <span>{item.label}</span>
                              </NavLink>
                              <CollapsibleTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-auto flex size-6 items-center justify-center rounded-sm hover:bg-sidebar-accent"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ChevronDown className="size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                                </button>
                              </CollapsibleTrigger>
                            </div>
                          </SidebarMenuButton>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.children?.filter(filterByAccess).map((child) => (
                                <SidebarMenuSubItem key={child.to}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={location.pathname === child.to || location.pathname.startsWith(child.to + '/')}
                                  >
                                    <NavLink to={child.to} onClick={handleMobileNavClick}>
                                      <child.icon className="size-4" />
                                      <span>{child.label}</span>
                                    </NavLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  }

                  // Item without children - render as before
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.label}
                        isActive={location.pathname === item.to}
                      >
                        <NavLink to={item.to} onClick={handleMobileNavClick}>
                          <item.icon />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
